import React from "react";
import { Link } from "react-router-dom";
import { 
  ChevronRight, 
  Home, 
  AlertTriangle,
  ArrowRight,
  Phone,
  Search,
  Heart,
  Brain,
  Shield,
  Activity,
  Clock,
  AlertCircle,
  Pill
} from "lucide-react";
import { Header, Footer } from "../../../components/Layout";

const MedicamentosPage = () => {
  const commonMedications = [
    {
      type: "Benzodiazepinas",
      examples: "Clonazepam, Alprazolam, Diazepam, Lorazepam",
      description: "Tranquilizantes prescritos para ansiedad e insomnio. Generan dependencia física rápidamente.",
      risk: "Alto"
    },
    {
      type: "Opioides",
      examples: "Tramadol, Codeína, Morfina, Oxicodona",
      description: "Analgésicos potentes. La tolerancia se desarrolla rápido, llevando a aumentar dosis.",
      risk: "Muy alto"
    },
    {
      type: "Estimulantes",
      examples: "Metilfenidato (Ritalin), Anfetaminas",
      description: "Usados para TDAH. Pueden ser mal utilizados para rendimiento académico/laboral.",
      risk: "Moderado-Alto"
    },
    {
      type: "Hipnóticos",
      examples: "Zolpidem, Zopiclona",
      description: "Medicamentos para dormir. Generan dependencia y tolerancia con uso prolongado.",
      risk: "Moderado"
    }
  ];

  const warningSigns = [
    { title: "Uso más allá de lo prescrito", description: "Tomar más cantidad o por más tiempo del indicado por el médico" },
    { title: "Búsqueda de múltiples recetas", description: "Visitar varios médicos para obtener más medicamentos" },
    { title: "Automedicación creciente", description: "Aumentar la dosis por cuenta propia sin consultar" },
    { title: "Preocupación por el suministro", description: "Ansiedad cuando el medicamento se está acabando" },
    { title: "Síntomas de abstinencia", description: "Malestar físico o emocional al intentar reducir la dosis" },
    { title: "Uso para otros fines", description: "Tomar el medicamento para efectos diferentes al original" }
  ];

  const treatmentOptions = [
    {
      title: "Reducción Gradual Supervisada",
      description: "Disminución progresiva de la dosis bajo supervisión médica. Esencial para evitar síndrome de abstinencia peligroso.",
      icon: Activity
    },
    {
      title: "Desintoxicación Médica",
      description: "Para casos severos, especialmente con benzodiazepinas y opioides. Requiere supervisión 24/7.",
      icon: Shield
    },
    {
      title: "Terapia Psicológica",
      description: "Abordar las causas subyacentes: ansiedad, dolor crónico, insomnio. TCC muy efectiva.",
      icon: Brain
    },
    {
      title: "Tratamiento Integral",
      description: "Manejo de la condición original más la dependencia. Enfoque multidisciplinario.",
      icon: Heart
    }
  ];

  const pageSchema = {
    "@context": "https://schema.org",
    "@type": "MedicalCondition",
    "name": "Adicción a Medicamentos",
    "alternateName": ["Dependencia a fármacos", "Abuso de medicamentos recetados"],
    "description": "La adicción a medicamentos recetados es una forma creciente de dependencia que afecta a personas de todas las edades."
  };

  const breadcrumbSchema = {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    "itemListElement": [
      { "@type": "ListItem", "position": 1, "name": "Inicio", "item": "https://sinadicciones.org" },
      { "@type": "ListItem", "position": 2, "name": "Adicciones", "item": "https://sinadicciones.org/adicciones" },
      { "@type": "ListItem", "position": 3, "name": "Medicamentos", "item": "https://sinadicciones.org/adicciones/medicamentos" }
    ]
  };

  return (
    <div className="min-h-screen bg-slate-950">
      
        <title>Adicción a Medicamentos: Benzodiazepinas, Opioides y más | Sin Adicciones</title>
        <meta name="description" content="Información sobre la adicción a medicamentos recetados en Chile: benzodiazepinas, opioides, hipnóticos. Señales de alerta, riesgos y opciones de tratamiento." />
        <link rel="canonical" href="https://sinadicciones.org/adicciones/medicamentos" />
        <meta property="og:image" content="https://sinadicciones.org/og-image.png" />
        <meta property="og:image:width" content="1280" />
        <meta property="og:image:height" content="896" />
        <meta property="og:type" content="website" />
        <meta property="og:url" content="https://sinadicciones.org/adicciones/medicamentos" />
        <meta property="og:site_name" content="SinAdicciones.org" />
        <meta property="og:locale" content="es_CL" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:image" content="https://sinadicciones.org/og-image.png" />

        <script type="application/ld+json">{JSON.stringify(pageSchema)}</script>
        <script type="application/ld+json">{JSON.stringify(breadcrumbSchema)}</script>
      

      <Header />
      <div className="h-16 md:h-20" />

      {/* Breadcrumb */}
      <div className="bg-slate-900 border-b border-slate-800">
        <div className="max-w-7xl mx-auto px-4 py-3">
          <nav className="flex items-center gap-2 text-sm text-slate-400">
            <Link to="/" className="hover:text-emerald-400 transition-colors flex items-center gap-1">
              <Home className="w-4 h-4" />
              Inicio
            </Link>
            <ChevronRight className="w-4 h-4" />
            <Link to="/adicciones" className="hover:text-emerald-400 transition-colors">
              Adicciones
            </Link>
            <ChevronRight className="w-4 h-4" />
            <span className="text-slate-300">Medicamentos</span>
          </nav>
        </div>
      </div>

      {/* Hero */}
      <section className="relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-blue-900/20 via-slate-900 to-slate-950" />
        <div className="absolute inset-0">
          <div className="absolute top-20 left-10 w-72 h-72 bg-blue-500/10 rounded-full blur-3xl" />
        </div>
        
        <div className="relative max-w-7xl mx-auto px-4 py-16 md:py-20">
          <div className="max-w-4xl">
            <div className="flex items-center gap-3 mb-6">
              <span className="text-5xl">💊</span>
              <div className="px-4 py-2 bg-blue-500/10 border border-blue-500/20 rounded-full">
                <span className="text-blue-400 font-medium">En aumento post-pandemia</span>
              </div>
            </div>
            
            <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold text-white mb-6 leading-tight">
              Adicción a{" "}
              <span className="text-transparent bg-clip-text bg-gradient-to-r from-blue-400 to-cyan-400">
                Medicamentos Recetados
              </span>
            </h1>
            
            <p className="text-lg md:text-xl text-slate-300 mb-8 max-w-3xl">
              Una adicción que crece silenciosamente. Los medicamentos recetados por un médico pueden generar 
              dependencia severa si no se usan correctamente. Reconocer el problema es el primer paso.
            </p>
            
            <div className="flex flex-wrap gap-4">
              <Link 
                to="/profesionales"
                className="px-6 py-3 bg-blue-500 hover:bg-blue-600 text-white font-semibold rounded-xl transition-all flex items-center gap-2"
              >
                <Search className="w-5 h-5" />
                Buscar Especialista
              </Link>
              <a 
                href="tel:1412"
                className="px-6 py-3 bg-slate-800 hover:bg-slate-700 text-white font-semibold rounded-xl transition-all border border-slate-700 flex items-center gap-2"
              >
                <Phone className="w-5 h-5" />
                Fono Drogas: 1412
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* Warning Banner */}
      <section className="bg-yellow-500/10 border-y border-yellow-500/20 py-6">
        <div className="max-w-7xl mx-auto px-4">
          <div className="flex items-center gap-4">
            <AlertCircle className="w-8 h-8 text-yellow-400 flex-shrink-0" />
            <div>
              <h3 className="text-yellow-400 font-bold">Importante</h3>
              <p className="text-slate-300">
                Nunca dejes de tomar un medicamento abruptamente sin supervisión médica, especialmente benzodiazepinas y opioides.
                La abstinencia puede ser peligrosa e incluso mortal. Consulta siempre con un profesional.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Common Medications */}
      <section className="py-16">
        <div className="max-w-7xl mx-auto px-4">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-white mb-4">
              Medicamentos con Mayor Potencial Adictivo
            </h2>
            <p className="text-slate-400 max-w-2xl mx-auto">
              Estos medicamentos, aunque útiles cuando se usan correctamente, pueden generar dependencia.
            </p>
          </div>

          <div className="grid md:grid-cols-2 gap-6">
            {commonMedications.map((med, index) => (
              <div 
                key={index}
                className={`p-6 rounded-2xl border ${
                  med.risk === 'Muy alto' ? 'bg-red-500/10 border-red-500/30' :
                  med.risk === 'Alto' ? 'bg-orange-500/10 border-orange-500/30' :
                  'bg-yellow-500/10 border-yellow-500/30'
                }`}
              >
                <div className="flex items-center justify-between mb-3">
                  <h3 className="text-xl font-bold text-white">{med.type}</h3>
                  <span className={`text-xs px-3 py-1 rounded-full font-medium ${
                    med.risk === 'Muy alto' ? 'bg-red-500/20 text-red-400' :
                    med.risk === 'Alto' ? 'bg-orange-500/20 text-orange-400' :
                    'bg-yellow-500/20 text-yellow-400'
                  }`}>
                    Riesgo {med.risk}
                  </span>
                </div>
                <p className="text-slate-500 text-sm mb-2">{med.examples}</p>
                <p className="text-slate-300">{med.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Warning Signs */}
      <section className="py-16 bg-slate-900/50">
        <div className="max-w-7xl mx-auto px-4">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-white mb-4">
              Señales de Dependencia
            </h2>
            <p className="text-slate-400 max-w-2xl mx-auto">
              ¿Reconoces alguna de estas señales en ti o en un ser querido?
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {warningSigns.map((sign, index) => (
              <div 
                key={index}
                className="p-6 bg-slate-800/50 rounded-2xl border border-blue-500/20"
              >
                <div className="flex items-start gap-4">
                  <AlertTriangle className="w-6 h-6 text-blue-400 flex-shrink-0 mt-1" />
                  <div>
                    <h3 className="text-lg font-bold text-white mb-2">{sign.title}</h3>
                    <p className="text-slate-400 text-sm">{sign.description}</p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Treatment */}
      <section className="py-16">
        <div className="max-w-7xl mx-auto px-4">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-white mb-4">
              Opciones de Tratamiento
            </h2>
            <p className="text-slate-400 max-w-2xl mx-auto">
              El tratamiento de la dependencia a medicamentos requiere un enfoque médico cuidadoso y gradual.
            </p>
          </div>

          <div className="grid md:grid-cols-2 gap-6">
            {treatmentOptions.map((option, index) => (
              <div 
                key={index}
                className="p-6 bg-slate-800/50 rounded-2xl border border-slate-700/50 hover:border-emerald-500/30 transition-colors"
              >
                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 rounded-xl bg-emerald-500/20 flex items-center justify-center flex-shrink-0">
                    <option.icon className="w-6 h-6 text-emerald-400" />
                  </div>
                  <div>
                    <h3 className="text-xl font-bold text-white mb-2">{option.title}</h3>
                    <p className="text-slate-400">{option.description}</p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-16 bg-slate-900/50">
        <div className="max-w-4xl mx-auto px-4">
          <div className="relative overflow-hidden rounded-3xl bg-gradient-to-br from-emerald-900/50 to-slate-800/50 border border-emerald-500/20 p-8 md:p-12 text-center">
            <Heart className="w-16 h-16 text-emerald-400 mx-auto mb-6" />
            <h2 className="text-2xl md:text-3xl font-bold text-white mb-4">
              La Recuperación Comienza con una Consulta
            </h2>
            <p className="text-slate-300 mb-8 max-w-2xl mx-auto">
              Un profesional puede ayudarte a diseñar un plan de reducción seguro y tratar las causas subyacentes de la dependencia.
            </p>
            
            <div className="flex flex-wrap justify-center gap-4">
              <Link 
                to="/profesionales"
                className="px-8 py-4 bg-emerald-500 hover:bg-emerald-600 text-white font-bold rounded-xl transition-all shadow-lg shadow-emerald-500/25 flex items-center gap-2"
              >
                <Search className="w-5 h-5" />
                Buscar Profesional
              </Link>
              <Link 
                to="/asesoria"
                className="px-8 py-4 bg-slate-800 hover:bg-slate-700 text-white font-semibold rounded-xl transition-all border border-slate-700"
              >
                Asesoría Experta
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Related */}
      <section className="py-16">
        <div className="max-w-7xl mx-auto px-4">
          <h2 className="text-2xl font-bold text-white mb-6">Contenido Relacionado</h2>
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
            {[
              { title: "Alcoholismo", path: "/adicciones/alcohol", icon: "🍺" },
              { title: "Tratamiento Ambulatorio", path: "/tratamiento-ambulatorio", icon: "🏥" },
              { title: "Centros Especializados", path: "/centros-de-rehabilitacion-drogas-chile", icon: "🏛️" },
              { title: "Tests de Evaluación", path: "/herramientas", icon: "📋" }
            ].map((item, index) => (
              <Link 
                key={index}
                to={item.path}
                className="flex items-center gap-3 p-4 bg-slate-800/50 rounded-xl border border-slate-700/50 hover:border-emerald-500/30 transition-all group"
              >
                <span className="text-2xl">{item.icon}</span>
                <span className="text-white group-hover:text-emerald-400 transition-colors">{item.title}</span>
              </Link>
            ))}
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default MedicamentosPage;
